import { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import deyeLogo from "../../../../shared/oversight-core/assets/images/deye-logo.png";
import goodweLogo from "../../../../shared/oversight-core/assets/images/goodwe-logo.svg";
import growattLogo from "../../../../shared/oversight-core/assets/images/growatt-logo.png";
import victronLogo from "../../../../shared/oversight-core/assets/images/victron-energy-logo.png";
import { IShallowSolarPlantView } from "../../../../shared/oversight-core/interfaces/shallow-solar-plant-view";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import styles from "./add-update-site-modal.module.scss";

interface IProps extends ModelContainerProps {
  show: boolean;
  onLinkSolarPlant: (brandId?: string) => void;
  solarBrandList: Option[];
  setSelectedPlant: React.Dispatch<
    React.SetStateAction<IShallowSolarPlantView | null>
  >;
  selectedPlant: IShallowSolarPlantView | null;
}

interface FormValues {
  siteName: string;
  client: string;
  location: string;
  inverterBrand: Option | [];
}

const defaultFormValues: FormValues = {
  siteName: "",
  client: "",
  location: "",
  inverterBrand: [],
};

const AddUpdateSiteModal = (props: IProps) => {
  const {
    show,
    selectedPlant,
    setSelectedPlant,
    solarBrandList,
    onLinkSolarPlant,
  } = props;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { ...defaultFormValues },
  });

  const watchInverterBrand = watch("inverterBrand") as Option;

  const brandOptions: Option[] = useMemo(() => {
    return (solarBrandList || []).map((brand) => {
      let icon = null;
      if (brand.data === "GOODWE") icon = goodweLogo;
      else if (brand.data === "Growatt") icon = growattLogo;
      else if (brand.data === "DEYE") icon = deyeLogo;
      else if (brand.data === "VRM") icon = victronLogo;

      return {
        ...brand,
        label: icon ? (
          <div className="d-flex align-items-center">
            <img
              src={icon}
              alt={brand.label as string}
              height={10}
              className="me-2"
            />
            {brand.label}
          </div>
        ) : (
          brand.label
        ),
      };
    });
  }, [solarBrandList]);

  useEffect(() => {
    if (selectedPlant?.generationServiceProviderId) {
      const foundBrand = brandOptions.find(
        (b) => b.value === selectedPlant.generationServiceProviderId,
      );
      if (foundBrand) {
        setValue("inverterBrand", foundBrand);
      }
    }
  }, [selectedPlant, brandOptions, setValue]);

  const onSubmit = (data: FormValues) => {
    console.log(data);
  };

  return (
    <ModalContainer
      show={show}
      title="Add Site"
      onClose={() => {
        props.onClose?.();
        reset({ ...defaultFormValues });
      }}
      onCancel={() => {
        props.onCancel?.();
        reset({ ...defaultFormValues });
      }}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      cancelButtonVariant="transparent"
      size="modal-lg"
    >
      <>
        <AppInput
          label="Site Name"
          placeholder="Site Name"
          name="siteName"
          register={register("siteName", {
            required: "Site name is required",
          })}
          errors={errors}
        />
        <AppInput
          className="mt-4"
          label="Client"
          placeholder="Client"
          name="client"
          register={register("client", { required: "Client is required" })}
          errors={errors}
        />
        <AppInput
          className="mt-4"
          label="Location"
          placeholder="Location"
          name="location"
          register={register("location", {
            required: "Location is required",
          })}
          icon="location_on"
          errors={errors}
        />
        <AppSelect
          className="mt-4"
          label="Inverter Brand"
          defaultValue={""}
          placeholder="Inverter Brand"
          options={brandOptions}
          control={control}
          name="inverterBrand"
          errors={errors}
          register={register("inverterBrand", {
            required: "Inverter Brand is required",
          })}
          isRequired
        />
        {selectedPlant ? (
          <div className={`${styles.cardContainer} px-3 py-2 mt-3`}>
            <Row className="align-items-center">
              <Col>
                <Row>
                  <Col className="text-light font-weight-500 font-size-12">
                    {selectedPlant.plantId}
                  </Col>
                </Row>
                <Row>
                  <Col className="text-dark font-weight-500 font-size-16">
                    {selectedPlant.plantName}
                  </Col>
                </Row>
                <Row>
                  <Col className="text-light font-weight-400 font-size-10">
                    {selectedPlant.address}
                  </Col>
                </Row>
              </Col>
              <Col className="col-auto">
                <MaterialIcon
                  icon="link_off"
                  color="#D84560"
                  className="cursor-pointer"
                  onClick={() => setSelectedPlant(null)}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <Row className="justify-content-center mt-4">
            <Col
              className="col-auto cursor-pointer"
              onClick={() => onLinkSolarPlant(watchInverterBrand?.value)}
            >
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <MaterialIcon icon="link" color="#2F2A89" />
                </Col>
                <Col className="text-light">Link Solar Plant</Col>
              </Row>
            </Col>
          </Row>
        )}
      </>
    </ModalContainer>
  );
};

export default AddUpdateSiteModal;
